import React, { useEffect, useMemo, useRef, useState } from "react"
import { Badge, Button, Container, Spinner } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import avatar from "../../assets/images/avatar.png"
import arrowDown from "../../assets/images/icons/arrow-down.svg"
import eye from "../../assets/images/icons/eye.svg"
import deleteIcon from "../../assets/images/icons/delete-red.svg"
import { Link } from "react-router-dom"
import {
  useDeleteIcdMutation,
  useLazyGetIcdsListQuery,
} from "store/api/icds/icds.api"
import DeleteConfirmationModal from "modals/deleteConfirmation"
import moment from "moment"
import {
  exportToExcel,
  formatAmount,
  getIcdRequestStatus,
  getTableSortingHeaderIcon,
} from "helpers/common"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"
import toast from "react-hot-toast"
import "flatpickr/dist/themes/material_blue.css"
import FlatPickr from "react-flatpickr"
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect/index"
import { icdRequestStatus } from "helpers/utils"

const defaultFilters = {
  search: "",
  page: 1,
  created_by: "INVESTOR",
  pageSize: 20,
  status: "",
  timestamp: "",
  created_at: "",
}

const ICDList = () => {
  const [getResults, { isFetching }] = useLazyGetIcdsListQuery()
  const [onDelete, { isLoading: isDeleting }] = useDeleteIcdMutation()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [isToast, setIsToast] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const deleteRef = useRef(null)
  const { hasPermission } = usePermission()

  //meta title
  document.title = "ICDs - Prachay"

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    getList()
  }, [filters])

  const onDeleteSuccess = () => {
    setIsToast(false)
    deleteRef.current = null
    setFilters((prev) => ({ ...prev, page: 1, timestamp: moment().unix() }))
  }

  const columns = useMemo(() => {
    const columns = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Date"),
        accessorKey: "formatted_created_at",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <DefaultAvatar
            avatar_url={row?.original.investor?.avatar_url}
            email={row?.original.investor?.email}
            name={row?.original.investor?.name}
          />
        ),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Request ID"),
        accessorKey: "unique_code",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="code-number">{row?.original.unique_code}</div>
        ),
      },

      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Amount (₹)"),
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>₹ {formatAmount(row?.original?.icdsRequest.amount)}</div>
        ),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Interest"),
        accessorKey: "interest_rate",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">{row?.original?.icdsRequest.interest_rate} %</div>
        ),
      },

      {
        header: ({ column }) =>
          getTableSortingHeaderIcon(column, "Date of Repayment"),
        accessorKey: "repayment_date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row?.original?.icdsRequest.repayment_date
              ? moment(row?.original?.icdsRequest.repayment_date).format(
                  "D MMM YYYY"
                )
              : ""}
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="code-status">
              {getIcdRequestStatus(row?.original?.icdsRequest.status)}
            </div>
          )
        },
        // cell: ({ row }) => (
        //   <div className="code-status">{getIcdsStatus(row?.original.status)}</div>
        // ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex justify-content-center align-items-center gap-3">
            <div className="action-view">
              <Link to={`/icd/view/${row?.original.id}`}>
                <img src={eye} alt="" />
              </Link>
            </div>
          </div>
        ),
      },
    ]

    const isView = hasPermission(PERMISSIONS.ICD_VIEW)
    if (isView) return columns
    return columns.filter((item) => item.id !== "actions")
  }, [hasPermission])

  const handleExport = async () => {
    try {
      setExcelLoading(true)
      await exportToExcel(
        "/icds",
        { ...filters, exportToExcel: true },
        `Investor-Requests-${moment().format("D-MMM-YYYY")}.xlsx`
      )
    } catch (err) {
      toast.error("Export failed. Please try again.")
    } finally {
      setExcelLoading(false)
    }
  }

  const FilterComponents = () => (
    <>
      <div className="">
        <select
          className="form-select"
          style={{
            minWidth: "150px",
          }}
          value={filters.status}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              page: 1,
              status: e.target.value,
            }))
          }
        >
          <option value={""}>Select Status</option>
          {icdRequestStatus.map((item, index) => (
            <option key={`icdRequestStatus_${index}`} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>
      </div>
      <FlatPickr
        className={`form-control`}
        style={{
          maxWidth: "200px",
        }}
        placeholder="Select Month"
        options={{
          plugins: [
            new monthSelectPlugin({
              shorthand: false,
              dateFormat: "Y-m",
              altFormat: "F Y",
              theme: "light",
            }),
          ],
          dateFormat: "M, Y",
          maxDate: new Date(),
        }}
        value={filters.created_at}
        onChange={(date) => {
          setFilters((prev) => ({
            ...prev,
            page: 1,
            created_at: date,
          }))
        }}
      />
      <Button className="btn btn-primary" onClick={() => handleExport()}>
        {excelLoading ? (
          <Spinner color={"#FFF"} size={"sm"} />
        ) : (
          <i className="mdi mdi-file-excel"></i>
        )}
      </Button>
    </>
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={"ICDs"} breadcrumbItem={"ICDs"} />

          <TableContainer
            isLoading={isFetching}
            count={count}
            filters={filters}
            setFilters={setFilters}
            columns={columns}
            data={data}
            isGlobalFilter={true}
            isCustomPageSize={true}
            FilterComponents={FilterComponents}
            isPagination={true}
            pagination="pagination"
            paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        </Container>

        {isToast && deleteRef && (
          <DeleteConfirmationModal
            open={isToast}
            id={deleteRef.current.id}
            setOpen={setIsToast}
            isLoading={isDeleting}
            onClose={() => {
              setIsToast(false)
              deleteRef.current = null
            }}
            title={"Delete ICD"}
            description="Are you sure you want to delete this icd?"
            onDelete={onDelete}
            onSuccess={onDeleteSuccess}
          />
        )}
      </div>
    </React.Fragment>
  )
}

export default ICDList
